import { useMemo } from "react";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AnimatedNumber } from "@/components/dashboard/AnimatedNumber";
import { formatDurationCompact, formatWeekdayShort, istDateKeyToDate } from "@/lib/format";

export type StudyTimeChartProps = {
  /** [{date: 'YYYY-MM-DD', seconds: number}] for the last 7 days, oldest first. */
  daily: { date: string; seconds: number }[];
};

/**
 * "This week / 3h 24m / study time" card with a clean monotone curve
 * underneath. The curve plots in (path draws progressively) via
 * recharts' built-in animation over ~1.4s after a short delay so the
 * eye lands on the number first. Hover anywhere on the curve to see
 * that day's value.
 *
 * Backend returns only days that had activity; we pre-fill a 7-day
 * window from today backwards here.
 */
export function StudyTimeChart({ daily }: StudyTimeChartProps) {
  // The backend sends a dense, IST-bucketed, exactly-7-element window whose sum
  // IS the headline total. There used to be a re-bucket here that rebuilt the
  // window from the browser clock in UTC and re-summed it -- which is why the
  // "This week" tile and the chart beside it disagreed, and why an IST browser
  // before 05:30 shifted the whole curve a column. Render what we're given.
  const data = useMemo(
    () => daily.map((d) => ({
      date: d.date,
      seconds: Math.max(0, Math.round(d.seconds)),
      label: formatWeekdayShort(istDateKeyToDate(d.date)),
    })),
    [daily],
  );

  const totalSeconds = useMemo(() => data.reduce((acc, d) => acc + d.seconds, 0), [data]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-border bg-surface p-5 sm:p-6 overflow-hidden"
    >
      <p className="text-xs sm:text-sm text-text-muted">This week</p>

      <AnimatedNumber
        value={totalSeconds}
        format={(n) => formatDurationCompact(Math.max(0, Math.round(n)))}
        durationMs={720}
        className="block mt-1 font-display text-3xl sm:text-4xl font-bold tabular-nums tracking-tight"
      />

      <p className="mt-1 text-sm text-text-muted">study time</p>

      {/* Curve. Uses recharts' built-in path animation so the line
          plots in along its length (not just a clip-path reveal).
          Hidden XAxis carries the weekday labels into the tooltip's
          labelFormatter but stays invisible on the canvas. */}
      <div className="mt-5 -mx-5 sm:-mx-6 -mb-5 sm:-mb-6 h-20 sm:h-24">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="study-fill-mini" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="rgb(var(--brand))" stopOpacity={0.22} />
                <stop offset="100%" stopColor="rgb(var(--brand))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" hide />
            <YAxis hide domain={[0, (max: number) => Math.max(60, max * 1.2)]} />
            <Tooltip
              cursor={{ stroke: "rgb(var(--brand))", strokeOpacity: 0.35, strokeWidth: 1 }}
              contentStyle={{
                background: "rgb(var(--surface))",
                border: "1px solid rgb(var(--border))",
                borderRadius: 10,
                fontSize: 12,
                color: "rgb(var(--text))",
                boxShadow: "0 4px 12px rgb(15 23 42 / 0.08)",
                padding: "6px 10px",
              }}
              labelStyle={{
                color: "rgb(var(--text-muted))",
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: 2,
              }}
              formatter={(v: number) => [formatDurationCompact(v), "Studied"]}
              labelFormatter={(label: string) => label}
            />
            <Area
              type="monotone"
              dataKey="seconds"
              stroke="rgb(var(--brand))"
              strokeWidth={2.5}
              strokeLinecap="round"
              fill="url(#study-fill-mini)"
              dot={false}
              activeDot={{
                r: 4,
                stroke: "rgb(var(--surface))",
                strokeWidth: 2,
                fill: "rgb(var(--brand))",
              }}
              // recharts' built-in path animation -- begin-to-end draw.
              isAnimationActive
              animationBegin={300}
              animationDuration={1400}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
}
